import { Blockchain, PaymentStandardType } from '@dfx.swiss/react';
import { C2BPaymentMethod, TransferMethod, WalletAppId, WalletInfo } from 'src/dto/payment-link.dto';

const EvmMethods: TransferMethod[] = [
  Blockchain.ETHEREUM,
  Blockchain.ARBITRUM,
  Blockchain.OPTIMISM,
  Blockchain.POLYGON,
  Blockchain.BASE,
  Blockchain.GNOSIS,
  Blockchain.BINANCE_SMART_CHAIN,
  Blockchain.HAQQ,
];

export const PaymentStandards = {
  [PaymentStandardType.OPEN_CRYPTO_PAY]: {
    id: PaymentStandardType.OPEN_CRYPTO_PAY,
    label: 'OpenCryptoPay.io',
    description: 'Pay with OpenCryptoPay, Bitcoin Lightning LNURL',
    paymentIdentifierLabel: 'URL',
  },
  [PaymentStandardType.LIGHTNING_BOLT11]: {
    id: PaymentStandardType.LIGHTNING_BOLT11,
    label: 'Bitcoin Lightning',
    description: 'Pay with a Bolt 11 Invoice',
    paymentIdentifierLabel: 'LNR',
  },

  [PaymentStandardType.PAY_TO_ADDRESS]: {
    id: PaymentStandardType.PAY_TO_ADDRESS,
    label: '{{blockchain}} address',
    description: 'Pay to a {{blockchain}} Blockchain address',
    paymentIdentifierLabel: 'URI',
  },
};

export const PaymentLinkWallets: WalletInfo[] = [
  // ----------------------- RECOMMENDED APPS -----------------------
  {
    id: WalletAppId.CAKEWALLET,
    name: 'Cake Wallet',
    websiteUrl: 'https://cakewallet.com/',
    iconUrl: 'https://dfx.swiss/images/app/CakeWallet.webp',
    deepLink: 'cakewallet:',
    appStoreUrl: 'https://apps.apple.com/app/cake-wallet-for-xmr-monero/id1334702542',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.cakewallet.cake_wallet',
    recommended: true,
    supportedMethods: [Blockchain.BITCOIN, Blockchain.MONERO, ...EvmMethods],
  },
  {
    id: WalletAppId.FRANKENCOIN,
    name: 'Frankencoin',
    websiteUrl: 'https://frankencoin.app/',
    iconUrl: 'https://dfx.swiss/images/app/Frankencoin.webp',
    appStoreUrl: 'https://apps.apple.com/app/frankencoin-wallet/id6480348701',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=swiss.dfx.frankencoin_wallet',
    recommended: true,
    supportedMethods: EvmMethods,
    supportedTokens: ['Ethereum:ZCHF', 'Polygon:ZCHF'],
  },
  {
    id: WalletAppId.PHOENIX,
    name: 'Phoenix',
    websiteUrl: 'https://phoenix.acinq.co/',
    iconUrl: 'https://dfx.swiss/images/app/Phoenix.webp',
    deepLink: 'phoenix:',
    appStoreUrl: 'https://apps.apple.com/app/phoenix-wallet/id1544097028',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=fr.acinq.phoenix.mainnet',
    recommended: true,
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.WALLETOFSATOSHI,
    name: 'Wallet of Satoshi',
    websiteUrl: 'https://www.walletofsatoshi.com/',
    iconUrl: 'https://dfx.swiss/images/app/WalletofSatoshi.webp',
    deepLink: 'walletofsatoshi:',
    appStoreUrl: 'https://apps.apple.com/app/wallet-of-satoshi/id1438599608',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.livingroomofsatoshi.wallet',
    recommended: true,
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.BTC_TARO,
    name: 'BtcTaro',
    websiteUrl: 'https://dfx.swiss/bitcoin.html',
    iconUrl: 'https://dfx.swiss/images/app/BTCTaroDFX.webp',
    deepLink: 'dfxtaro:',
    appStoreUrl: 'https://apps.apple.com/app/dfx-btc-taproot-asset-wallet/id6466037617',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=swiss.dfx.bitcoin',
    recommended: true,
    supportedMethods: [Blockchain.LIGHTNING],
  },
  // ----------------------- COMPATIBLE APPS -----------------------
  {
    id: WalletAppId.BITBANANA,
    name: 'BitBanana',
    websiteUrl: 'https://bitbanana.app/',
    iconUrl: 'https://dfx.swiss/images/app/BitBanana.webp',
    deepLink: 'lightning:',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=app.michaelwuensch.bitbanana',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.BITKIT,
    name: 'Bitkit',
    websiteUrl: 'https://bitkit.to/',
    iconUrl: 'https://dfx.swiss/images/app/Bitkit.webp',
    deepLink: 'bitkit:',
    appStoreUrl: 'https://apps.apple.com/app/bitkit-bitcoin-ln-wallet/id6502440655',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=to.bitkit',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.BLINK,
    name: 'Blink',
    websiteUrl: 'https://de.blink.sv/',
    deepLink: 'lightning:',
    iconUrl: 'https://dfx.swiss/images/app/Blink.webp',
    appStoreUrl: 'https://apps.apple.com/app/blink-bitcoin-beach-wallet/id1531383905',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.galoyapp',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.BLITZWALLET,
    name: 'Blitz Wallet',
    websiteUrl: 'https://blitz-wallet.com/',
    deepLink: 'lightning:',
    iconUrl: 'https://dfx.swiss/images/app/BlitzWalletApp.webp',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.blitzwallet',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.BLIXT,
    name: 'Blixt',
    websiteUrl: 'https://blixtwallet.com/',
    iconUrl: 'https://dfx.swiss/images/app/Blixt.webp',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.blixtwallet',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.BREEZ,
    name: 'Breez',
    websiteUrl: 'https://breez.technology/',
    iconUrl: 'https://dfx.swiss/images/app/Breez.webp',
    deepLink: 'breez:',
    appStoreUrl: 'https://apps.apple.com/app/breez-lightning-client-pos/id1463604142',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.breez.client',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.COINCORNER,
    name: 'CoinCorner',
    websiteUrl: 'https://www.coincorner.com/',
    iconUrl: 'https://dfx.swiss/images/app/CoinCorner.webp',
    deepLink: 'lightning:',
    appStoreUrl: 'https://apps.apple.com/app/coincorner-checkout/id1464880599',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.coincorner.app.crypt',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.DEUROWALLET,
    name: 'DEURO Wallet',
    websiteUrl: 'https://deuro.com/',
    iconUrl: 'https://dfx.swiss/images/app/DeuroWallet.webp',
    appStoreUrl: 'https://apps.apple.com/ch/app/deuro-wallet/id6746087643',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=eu.deurowallet.wallet',
    supportedMethods: EvmMethods,
    supportedTokens: ['dEURO'],
  },
  {
    id: WalletAppId.LIFPAY,
    name: 'LifPay',
    websiteUrl: 'https://lifpay.me/',
    iconUrl: 'https://dfx.swiss/images/app/LifPay.webp',
    deepLink: 'lifpay:',
    appStoreUrl: 'https://apps.apple.com/app/lifpay/id1645840182',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=flutter.android.LifePay',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.LIPAWALLET,
    name: 'LipaWallet',
    websiteUrl: 'https://lipa.swiss/',
    iconUrl: 'https://dfx.swiss/images/app/lipawallet.webp',
    appStoreUrl: 'https://apps.apple.com/app/lipa-wallet/id1658329527',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.getlipa.wallet',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.LNBITS,
    name: 'LNbits',
    websiteUrl: 'https://lnbits.com/',
    iconUrl: 'https://dfx.swiss/images/app/LNbits.webp',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.lnbits.app',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.AQUA,
    name: 'AQUA',
    websiteUrl: 'https://aquawallet.io/',
    iconUrl: 'https://dfx.swiss/images/app/aqua.webp',
    appStoreUrl: 'https://apps.apple.com/app/aqua-wallet/id6468594241',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=io.aquawallet.android',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.ONEKEY,
    name: 'OneKey',
    websiteUrl: 'https://onekey.so/',
    iconUrl: 'https://dfx.swiss/images/app/OneKey.webp',
    appStoreUrl: 'https://apps.apple.com/app/onekey-crypto-defi-wallet/id1609559473',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=so.onekey.app.wallet',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.POUCHPH,
    name: 'PouchPH',
    websiteUrl: 'https://pouch.ph/',
    iconUrl: 'https://dfx.swiss/images/app/Pouchph.webp',
    deepLink: 'pouch:',
    appStoreUrl: 'https://apps.apple.com/app/pouch-lightning-wallet/id1584404678',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=pouch.ph',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.ZEBEDEE,
    name: 'ZEBEDEE',
    websiteUrl: 'https://zbd.gg/',
    iconUrl: 'https://dfx.swiss/images/app/ZEBEDEE.webp',
    deepLink: 'zebedee:',
    appStoreUrl: 'https://apps.apple.com/app/zebedee-play-earn-shop/id1484394401',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=io.zebedee.wallet',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.ZEUS,
    name: 'Zeus',
    websiteUrl: 'https://zeusln.com/',
    iconUrl: 'https://dfx.swiss/images/app/Zeus.webp',
    deepLink: 'lightning:',
    appStoreUrl: 'https://apps.apple.com/app/zeus-wallet/id1456038895',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=app.zeusln.zeus',
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.BINANCE,
    name: 'Binance',
    iconUrl: 'https://dfx.swiss/images/app/BinanceApp.webp',
    deepLink: 'bnc:',
    appStoreUrl: 'https://apps.apple.com/app/binance-buy-bitcoin-crypto/id1436799971',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.binance.dev',
    recommended: true,
    supportedMethods: [C2BPaymentMethod.BINANCE_PAY],
  },
  {
    id: WalletAppId.MUUN,
    name: 'Muun',
    websiteUrl: 'https://muun.com/',
    iconUrl: 'https://dfx.swiss/images/app/Muun.webp',
    appStoreUrl: 'https://apps.apple.com/us/app/muun-wallet/id1482037683',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=io.muun.apollo',
    deepLink: 'muun:',
    semiCompatible: true,
    supportedMethods: [Blockchain.LIGHTNING],
  },
  {
    id: WalletAppId.KUCOINPAY,
    name: 'Kucoin Pay',
    websiteUrl: 'https://kucoin.com/',
    iconUrl: 'https://dfx.swiss/images/app/KucoinApp.webp',
    appStoreUrl: 'https://kucoin-ios.onelink.me/L1k4/18p1goqs',
    playStoreUrl: 'https://kucoin-android.onelink.me/xTQQ/pvve7hp8',
    semiCompatible: true,
    deepLink: 'kucoinpay:',
    supportedMethods: [C2BPaymentMethod.KUCOINPAY],
  },
  {
    id: WalletAppId.BRIDGEWALLET,
    name: 'Bridge Wallet',
    websiteUrl: 'https://www.mtpelerin.com/de/bridge-wallet',
    iconUrl: 'https://dfx.swiss/images/app/bridge-wallet-icon.webp',
    deepLink: 'bridgewallet:',
    appStoreUrl: 'https://apps.apple.com/app/bridge-wallet/id1481859680',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.mtpelerin.bridge',
    semiCompatible: true,
    supportedMethods: [Blockchain.BITCOIN, Blockchain.LIGHTNING, ...EvmMethods],
  },
];
